/**
 * Platform detection and store links for the storefront's mobile app banner.
 * Kept free of DOM access so the rules can run under `bun test`.
 */

import { stripMarketPrefix } from './markets';

export type MobileAppBannerPlatform = 'android' | 'ios';

const ANDROID_PACKAGE = 'com.workmanusa.mobile';
const APPLE_APP_STORE_ID = '6793287882';
/** URL scheme registered by the production app; native routes hang off it. */
const APP_SCHEME = 'workman';

export const GOOGLE_PLAY_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
export const APP_LINK_HOST = 'app.workmanusa.com';
export const APP_LINK_ORIGIN = `https://${APP_LINK_HOST}`;
export const APP_STORE_URL = `https://apps.apple.com/app/id${APPLE_APP_STORE_ID}`;

/** Single-segment storefront paths that map to an app screen of the same name. */
const NATIVE_ROOT_ROUTES = new Set([
	'account',
	'cart',
	'drop',
	'explore',
	'orders',
	'wishlist',
	'stores',
	'trades',
]);

/** Storefront path head → native route prefix the app resolves. */
const NATIVE_ROUTE_PREFIXES: Readonly<Record<string, `/${string}`>> = {
	products: '/product',
	collections: '/collection',
	pages: '/page',
};

/**
 * CMS pages the app renders as a dedicated native screen instead of the
 * generic `/page/<slug>` block renderer. The nav's "Shop by Trade" link is the
 * CMS page `/pages/search-by-trade`, whose layout is a four-tile teaser; the
 * app's `/trades` screen is the full searchable index. `null` means the app's
 * home screen. Mirrors `NATIVE_CMS_PAGE_ROUTES` in the app's push-route.ts.
 */
const NATIVE_CMS_PAGE_ROUTES: Readonly<Record<string, `/${string}` | null>> = {
	home: null,
	'search-by-trade': '/trades',
	'top-secret': '/drop',
};

/**
 * Maps the storefront URL the shopper is on to the native screen the app
 * should open, e.g. `/en/ca/products/hi-vis-tee` → `/product/hi-vis-tee` and
 * `/cart` → `/cart`. Returns `null` for the home page (the app opens on its
 * home screen anyway) and for pages without a native counterpart (search,
 * policies, deeper account URLs).
 */
export function nativeRouteFromPathname(pathname: string): `/${string}` | null {
	const [head, handle, ...rest] = stripMarketPrefix(pathname).split('/').filter(Boolean);
	if (!head || rest.length > 0) return null;
	if (!handle) return NATIVE_ROOT_ROUTES.has(head) ? `/${head}` : null;
	if (head === 'pages' && handle in NATIVE_CMS_PAGE_ROUTES) {
		return NATIVE_CMS_PAGE_ROUTES[handle] ?? null;
	}
	const prefix = NATIVE_ROUTE_PREFIXES[head];
	return prefix ? `${prefix}/${handle}` : null;
}

export type MobileAppBannerLink = {
	href: string;
	label: string;
	cta: string;
};

/**
 * Where the banner's button goes.
 *
 * Android: an intent URL. Installed copies open straight to `nativeRoute`
 * through the workman scheme; otherwise Chrome falls back to Google Play.
 * The explicit package keeps another app from claiming the scheme.
 *
 * iOS: a verified HTTPS link on a separate host opens the installed app.
 * If iOS leaves the link in the browser, that host redirects to the App Store.
 * Never probe a custom scheme: Safari alerts when no installed app claims it.
 * Safari itself never sees this button; it gets Apple's banner instead (see
 * `buildAppleSmartAppBanner`).
 */
export function buildMobileAppBannerLink(
	platform: MobileAppBannerPlatform,
	nativeRoute: `/${string}` | null,
): MobileAppBannerLink {
	if (platform === 'android') {
		const intentPath = nativeRoute ? nativeRoute.replace(/^\//, '') : '/';
		return {
			href: `intent://${intentPath}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(GOOGLE_PLAY_URL)};end`,
			label: 'Open Workman app or install from Google Play',
			cta: 'Open',
		};
	}
	return {
		href: buildIosAppLink(nativeRoute),
		label: 'Open the Workman app or get it from the App Store',
		cta: 'Get',
	};
}

/** Product token the native app appends to its WebView user agents, e.g. `WorkmanApp/1.1.0`. */
const WORKMAN_APP_UA_PATTERN = /\bWorkmanApp\//;
const ANDROID_UA_PATTERN = /Android/i;
/** Android WebViews include `wv`. */
const ANDROID_WEBVIEW_UA_PATTERN = /; wv\)/i;
const IOS_UA_PATTERN = /iPhone|iPad|iPod/i;
/**
 * Safari's own `Version/x … Safari/y` tokens. Third-party iOS browsers built on
 * WebKit copy them, so Safari is the agents that carry both and none of the
 * third-party markers below. In-app browsers (Instagram, Facebook, stock
 * WKWebViews) omit `Version/`, which already rules them out.
 */
const SAFARI_VERSION_UA_PATTERN = /\bVersion\/[\d.]+/;
const SAFARI_UA_PATTERN = /\bSafari\//;
/** Third-party iOS browsers that reuse Safari's tokens; Safari itself never carries these. */
const NON_SAFARI_IOS_BROWSER_UA_PATTERN =
	/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|Ddg\/|DuckDuckGo|YaBrowser|GSA\//;

export type MobileAppBannerNavigatorHints = {
	userAgent: string;
	/** `navigator.platform`; iPadOS reports `MacIntel` when requesting desktop sites. */
	platform?: string;
	/** `navigator.maxTouchPoints`; separates iPadOS desktop-mode from real Macs. */
	maxTouchPoints?: number;
};

/**
 * Which app store the shopper's device uses, or `null` when there is nothing
 * to promote: desktops, Android WebViews (intent links are unreliable there),
 * and the Workman app's own WebViews (never promote the app inside the app).
 * The `/app` landing page orders its store buttons with this.
 */
export function detectMobileStorePlatform(
	hints: MobileAppBannerNavigatorHints,
): MobileAppBannerPlatform | null {
	const ua = hints.userAgent;

	// The Workman app tags its own WebViews (see apps/mobile/src/lib/web-view-user-agent.ts).
	// Third-party in-app browsers with a stock WKWebView agent still get the banner.
	if (WORKMAN_APP_UA_PATTERN.test(ua)) return null;

	if (ANDROID_UA_PATTERN.test(ua)) {
		return ANDROID_WEBVIEW_UA_PATTERN.test(ua) ? null : 'android';
	}

	const isIpadDesktopMode = hints.platform === 'MacIntel' && (hints.maxTouchPoints ?? 0) > 1;
	if (!IOS_UA_PATTERN.test(ua) && !isIpadDesktopMode) return null;

	return 'ios';
}

/**
 * Safari on iOS/iPadOS, which draws Apple's own banner at the top of the page:
 * the Smart App Banner from the page's `itunes` metadata, or its "Open in the
 * Workman app" universal-link banner once the app is installed (Safari shows
 * that one for every site the app claims through `applinks`, meta tag or
 * not). The in-page banner must stay hidden there or the two stack. Browsers
 * that present Safari's exact agent (Brave, SFSafariViewController) count as
 * Safari. Only meaningful once the agent is known to be iOS: macOS Safari
 * carries the same tokens.
 */
export function isIosSafari(userAgent: string): boolean {
	return (
		SAFARI_VERSION_UA_PATTERN.test(userAgent) &&
		SAFARI_UA_PATTERN.test(userAgent) &&
		!NON_SAFARI_IOS_BROWSER_UA_PATTERN.test(userAgent)
	);
}

/**
 * Which app store the in-page banner should promote, or `null` when the banner
 * should stay hidden: everything `detectMobileStorePlatform` rules out, plus
 * iOS Safari, where Apple's banner takes its place (see `isIosSafari`).
 */
export function detectMobileAppBannerPlatform(
	hints: MobileAppBannerNavigatorHints,
): MobileAppBannerPlatform | null {
	const platform = detectMobileStorePlatform(hints);
	return platform === 'ios' && isIosSafari(hints.userAgent) ? null : platform;
}

/** `<html>` attribute carrying the platform the banner promotes while it is up. */
export const MOBILE_APP_BANNER_ATTRIBUTE = 'data-mobile-app-banner';
/** sessionStorage flag set when the shopper closes the banner. */
export const MOBILE_APP_BANNER_DISMISS_KEY = 'workman-mobile-app-banner-dismissed-v1';
/**
 * Matches the banner's white background so the browser chrome above it (iOS
 * status bar, Android toolbar) does not stay tinted by the announcement bar
 * while the banner sits between them.
 */
export const MOBILE_APP_BANNER_THEME_COLOR = '#ffffff';

/**
 * Inline script the banner component renders just ahead of its markup. It
 * makes the same call as `detectMobileAppBannerPlatform` (plus the installed
 * web app and dismissed checks) while the HTML is still parsing, tagging
 * `<html>` with the platform so the CSS can reveal the banner in the first
 * paint. A string, not a bundle import, because it has to run before any
 * bundle loads; the test suite runs it against the same user agents as the
 * function so the two cannot drift apart.
 */
export const MOBILE_APP_BANNER_BOOT_SCRIPT = [
	'(function(){',
	'var n=navigator,ua=n.userAgent,platform=null;',
	`if(${WORKMAN_APP_UA_PATTERN}.test(ua))return;`,
	`if(${ANDROID_UA_PATTERN}.test(ua)){if(${ANDROID_WEBVIEW_UA_PATTERN}.test(ua))return;platform="android"}`,
	`else if(${IOS_UA_PATTERN}.test(ua)||(n.platform==="MacIntel"&&n.maxTouchPoints>1)){`,
	`if(${SAFARI_VERSION_UA_PATTERN}.test(ua)&&${SAFARI_UA_PATTERN}.test(ua)&&!${NON_SAFARI_IOS_BROWSER_UA_PATTERN}.test(ua))return;`,
	'platform="ios"}',
	'if(!platform)return;',
	'if(window.matchMedia("(display-mode: standalone)").matches)return;',
	`try{if(sessionStorage.getItem(${JSON.stringify(MOBILE_APP_BANNER_DISMISS_KEY)})==="1")return}catch(e){}`,
	`document.documentElement.setAttribute(${JSON.stringify(MOBILE_APP_BANNER_ATTRIBUTE)},platform);`,
	'var meta=document.createElement("meta");',
	'meta.name="theme-color";',
	`meta.content=${JSON.stringify(MOBILE_APP_BANNER_THEME_COLOR)};`,
	`meta.setAttribute(${JSON.stringify(MOBILE_APP_BANNER_ATTRIBUTE)},"");`,
	'document.head.prepend(meta);',
	'})();',
].join('');

/** Use storefront paths already covered by AASA and the native URL resolver. */
export function buildIosAppLink(nativeRoute: `/${string}` | null): string {
	const pathname = (nativeRoute ?? '/').replace(/^\/(product|collection|page)\//, '/$1s/');
	return `${APP_LINK_ORIGIN}${pathname}`;
}

/** Shape of Next's `Metadata['itunes']`, rendered as `<meta name="apple-itunes-app">`. */
export type AppleSmartAppBanner = { appId: string; appArgument?: string };

/**
 * Safari's Smart App Banner, driven by the page's `itunes` metadata. It is the
 * only app promotion Safari shows (the in-page banner hides itself there, see
 * `isIosSafari`): "View" opens the App Store listing and, once the app is
 * installed, "Open" launches it on `nativeRoute`. The argument uses the
 * registered `workman://` scheme, which is safe here because Safari only
 * offers "Open" when an installed app claims it. Next merges `itunes`
 * shallowly per route, so a page passing a route must restate the id.
 */
export function buildAppleSmartAppBanner(
	nativeRoute: `/${string}` | null = null,
): AppleSmartAppBanner {
	return nativeRoute
		? { appId: APPLE_APP_STORE_ID, appArgument: `${APP_SCHEME}:/${nativeRoute}` }
		: { appId: APPLE_APP_STORE_ID };
}
